import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getTextField } from '@lender/utils';
import { useTheme } from '@mui/material/styles';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { getSharedFormStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import FormGeneratorRichTextEditor from 'app/shared/componentsRestyled/common/formGenerator/FormGeneratorRichTextEditor';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { object } from 'yup';

import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';

import { closeDialogAction } from 'store/global/actions';
import { editNotesDataAction } from 'store/loanApplications/actions';
import {
  selectEntityChangedPropsById,
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';

import { UnderwritingTestIds } from '../../Underwriting.testIds';

const schema = object().shape({
  notes: getTextField({ isRequired: false }),
});

const NotesForm = ({ notes = '', setIsLoading, isLoading, formRef }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useMemo(() => getSharedFormStyles(theme), [theme]);

  const loanId = useLoanId();

  const pendingChangesData = useSelector(selectEntityChangedPropsById(loanId));
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.NOTES));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FormNames.NOTES));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.NOTES));

  const isDisabledByLock = isLoading || isPageLocked;

  const isChangedData = !!pendingChangesData?.underwritingManualNotes;

  const { form, handleChange, validationErrors, setSchema, isFormChanged, validateForm } = useForm({
    notes,
  });

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      dispatch(
        editNotesDataAction({
          notes: form.notes,
          loanId,
          requestMetadata: { tabId: LoanApplicationTabId.LOAN },
        }),
      )
        .then(() => dispatch(closeDialogAction()))
        .finally(() => setIsLoading(false));
    },
    [dispatch, form, setIsLoading, validateForm, loanId],
  );

  return (
    <ModalFormWrapper
      formRef={formRef}
      isFormChanged={isFormChanged}
      isSubmitting={isLoading}
      onSubmit={onSubmit}
    >
      <FormGeneratorRichTextEditor
        autoFocus
        fullWidth
        multiline
        required
        className={clsx(isChangedData && classes.textFieldHighlight, 'mt-8 mb-24')}
        data-testid={UnderwritingTestIds.NOTES_FORM_NOTES_INPUT}
        disabled={isLoading}
        error={!!validationErrors.notes}
        helperText={validationErrors.notes}
        id="notes"
        isHidden={hiddenFieldsMap.notes}
        label="Notes"
        maxLength={2000}
        name="notes"
        readOnly={isDisabledByLock || lockedFieldsMap.notes}
        rows={16}
        type="text"
        value={form.notes}
        variant="outlined"
        onChange={handleChange}
      />
    </ModalFormWrapper>
  );
};

NotesForm.propTypes = {
  notes: PropTypes.string,
  ...WithLoadingPropTypes,
};

export default withLoading(NotesForm);
